// apps/client/src/config/featureFlags.ts
// Feature flag configuration for progressive rollout
// Jeff Dean Approach: Safe, incremental feature deployment

/**
 * Feature Flags Configuration
 *
 * Controls which features are visible/enabled in the application.
 * Allows for safe, incremental rollout without code deployments.
 *
 * Usage:
 * ```tsx
 * import { featureFlags } from '../config/featureFlags';
 *
 * if (featureFlags.enhancedFilters) {
 *   return <EnhancedFilters />;
 * }
 * return <StandardFilters />;
 * ```
 */

interface FeatureFlags {
  // Phase 2: Enhanced filtering
  enhancedFilters: boolean;           // Master toggle for all enhanced filters
  lenderFilter: boolean;              // Show lender dropdown
  commissionTypeFilter: boolean;      // Show commission type toggle
  comparisonMode: boolean;            // Show YoY/PoP comparison
  productTypeFilter: boolean;         // Show product type filter
  performanceBands: boolean;          // Show performance band filter
  dateRangeFilter: boolean;           // Show custom date range picker

  // Phase 3: Advanced KPIs
  yoyTrendIndicators: boolean;        // Show YoY trend arrows on stat cards
  advancedKPIs: boolean;              // Show Tier 2/3 KPIs
  kpiDrilldown: boolean;              // Enable KPI drill-down modals

  // Phase 4: Performance optimizations
  queryOptimization: boolean;         // Use optimized query strategies
  clientSideCaching: boolean;         // Enable browser caching
  prefetching: boolean;               // Prefetch next month's data

  // Phase 5: Advanced visualizations
  newVisualizations: boolean;         // Show new chart types
  heatmaps: boolean;                  // Enable heatmap visualizations
  waterfallCharts: boolean;           // Enable waterfall charts
  scatterPlots: boolean;              // Enable scatter plot analysis

  // Development/debugging
  debugMode: boolean;                 // Show debug information
  performanceMetrics: boolean;        // Show performance timing
}

/**
 * Default feature flag values
 * Override via environment variables or remote config
 */
const defaultFlags: FeatureFlags = {
  // Phase 2: Enhanced filtering - ENABLED for Office Dashboard
  enhancedFilters: true,
  lenderFilter: true,
  commissionTypeFilter: true,
  comparisonMode: false,              // Complex - enable later
  productTypeFilter: false,           // Requires backend data
  performanceBands: true,
  dateRangeFilter: false,             // Future enhancement

  // Phase 3: Advanced KPIs - DISABLED (not implemented yet)
  yoyTrendIndicators: false,
  advancedKPIs: false,
  kpiDrilldown: false,

  // Phase 4: Performance - DISABLED (not implemented yet)
  queryOptimization: false,
  clientSideCaching: false,
  prefetching: false,

  // Phase 5: Visualizations - DISABLED (not implemented yet)
  newVisualizations: false,
  heatmaps: false,
  waterfallCharts: false,
  scatterPlots: false,

  // Development - DISABLED in production
  debugMode: process.env.NODE_ENV === 'development',
  performanceMetrics: process.env.NODE_ENV === 'development',
};

/**
 * Environment-based overrides
 * Read from environment variables (REACT_APP_FEATURE_*)
 */
function getEnvFlag(key: string, defaultValue: boolean): boolean {
  const envKey = `REACT_APP_FEATURE_${key.toUpperCase()}`;
  const envValue = process.env[envKey];

  if (envValue === undefined) {
    return defaultValue;
  }

  return envValue === 'true' || envValue === '1';
}

/**
 * Feature flags with environment overrides
 */
export const featureFlags: FeatureFlags = {
  enhancedFilters: getEnvFlag('enhancedFilters', defaultFlags.enhancedFilters),
  lenderFilter: getEnvFlag('lenderFilter', defaultFlags.lenderFilter),
  commissionTypeFilter: getEnvFlag('commissionTypeFilter', defaultFlags.commissionTypeFilter),
  comparisonMode: getEnvFlag('comparisonMode', defaultFlags.comparisonMode),
  productTypeFilter: getEnvFlag('productTypeFilter', defaultFlags.productTypeFilter),
  performanceBands: getEnvFlag('performanceBands', defaultFlags.performanceBands),
  dateRangeFilter: getEnvFlag('dateRangeFilter', defaultFlags.dateRangeFilter),

  yoyTrendIndicators: getEnvFlag('yoyTrendIndicators', defaultFlags.yoyTrendIndicators),
  advancedKPIs: getEnvFlag('advancedKPIs', defaultFlags.advancedKPIs),
  kpiDrilldown: getEnvFlag('kpiDrilldown', defaultFlags.kpiDrilldown),

  queryOptimization: getEnvFlag('queryOptimization', defaultFlags.queryOptimization),
  clientSideCaching: getEnvFlag('clientSideCaching', defaultFlags.clientSideCaching),
  prefetching: getEnvFlag('prefetching', defaultFlags.prefetching),

  newVisualizations: getEnvFlag('newVisualizations', defaultFlags.newVisualizations),
  heatmaps: getEnvFlag('heatmaps', defaultFlags.heatmaps),
  waterfallCharts: getEnvFlag('waterfallCharts', defaultFlags.waterfallCharts),
  scatterPlots: getEnvFlag('scatterPlots', defaultFlags.scatterPlots),

  debugMode: getEnvFlag('debugMode', defaultFlags.debugMode),
  performanceMetrics: getEnvFlag('performanceMetrics', defaultFlags.performanceMetrics),
};

/**
 * Helper: Check if any enhanced filter is enabled
 */
export function hasEnhancedFilters(): boolean {
  return (
    featureFlags.enhancedFilters &&
    (featureFlags.lenderFilter ||
      featureFlags.commissionTypeFilter ||
      featureFlags.comparisonMode ||
      featureFlags.productTypeFilter ||
      featureFlags.performanceBands ||
      featureFlags.dateRangeFilter)
  );
}

/**
 * Helper: Log active feature flags (development only)
 */
if (process.env.NODE_ENV === 'development') {
  console.log('[FeatureFlags] Active flags:', {
    enhancedFilters: featureFlags.enhancedFilters,
    lenderFilter: featureFlags.lenderFilter,
    commissionTypeFilter: featureFlags.commissionTypeFilter,
    performanceBands: featureFlags.performanceBands,
  });
}
